import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { getLeague, updateLeague, rotateInviteCode, deleteLeague, DEFAULT_POINTS_CONFIG } from '../services/leagueService';
import { isSettingsLocked } from '../utils/lockUtils';
import './LeagueManagePage.css';

const ROUNDS = [
  { key: 'group', label: 'Group Stage' },
  { key: 'r32',   label: 'Round of 32' },
  { key: 'r16',   label: 'Round of 16' },
  { key: 'qf',    label: 'Quarterfinal' },
  { key: 'sf',    label: 'Semifinal' },
  { key: 'final', label: 'Final' },
  { key: 'third', label: '3rd Place' },
];

export default function LeagueManagePage() {
  const { leagueId } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [league, setLeague] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [notCommissioner, setNotCommissioner] = useState(false);

  // Edit state
  const [editName, setEditName] = useState('');
  const [nameSaved, setNameSaved] = useState(false);
  const [nameLoading, setNameLoading] = useState(false);

  const [editPoints, setEditPoints] = useState({ ...DEFAULT_POINTS_CONFIG });
  const [pointsSaved, setPointsSaved] = useState(false);
  const [pointsLoading, setPointsLoading] = useState(false);

  // Members
  const [members, setMembers] = useState(null);

  // Invite by email
  const [inviteEmail, setInviteEmail] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailMsg, setEmailMsg] = useState('');

  // Invite code rotation
  const [rotatingCode, setRotatingCode] = useState(false);

  // Delete league
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Feedback
  const [toast, setToast] = useState('');
  const [error, setError] = useState('');

  // Lock check — uses both Firestore flag and current time
  const isLocked = isSettingsLocked(league);

  // Load league
  const loadLeague = useCallback(async () => {
    if (!leagueId || !currentUser) return;
    try {
      const data = await getLeague(leagueId);
      if (!data) { setNotFound(true); return; }
      if (data.commissionerId !== currentUser?.uid) { setNotCommissioner(true); return; }
      setLeague(data);
      setEditName(data.name);
      setEditPoints({ ...DEFAULT_POINTS_CONFIG, ...data.pointsConfig });
    } catch (_) {
      setNotFound(true);
    }
  }, [leagueId, currentUser]);

  useEffect(() => { loadLeague(); }, [loadLeague]);

  // Load members with display names and pick counts
  useEffect(() => {
    if (!leagueId) return;
    loadMembers();
  }, [leagueId]); // eslint-disable-line

  async function loadMembers() {
    try {
    // Fetch all leagueMembers for this league
    const membersSnap = await getDocs(
      query(collection(db, 'leagueMembers'), where('leagueId', '==', leagueId))
    );
    const memberData = membersSnap.docs.map((d) => d.data());

    // Fetch displayNames for all members in parallel
    const withNames = await Promise.all(
      memberData.map(async (m) => {
        const userSnap = await getDoc(doc(db, 'users', m.userId));
        const username = userSnap.exists() ? userSnap.data().username : 'Unknown';
        return { ...m, username };
      })
    );

    // Sort by totalPoints desc, then joinedAt asc for stable ordering
    withNames.sort((a, b) => {
      const ptsDiff = (b.totalPoints || 0) - (a.totalPoints || 0);
      if (ptsDiff !== 0) return ptsDiff;
      // Earlier joinedAt = higher in list for ties
      const aTime = a.joinedAt?.seconds ?? 0;
      const bTime = b.joinedAt?.seconds ?? 0;
      return aTime - bTime;
    });

    // Compute shared rank
    let rank = 1;
    const ranked = withNames.map((m, i) => {
      if (i > 0 && (m.totalPoints || 0) < (withNames[i - 1].totalPoints || 0)) {
        rank = i + 1;
      }
      return { ...m, rank };
    });

    setMembers(ranked);
    } catch (err) {
      console.error('loadMembers error:', err);
      setMembers([]);
    }
  }

  // ── Save league name ──
  async function saveName() {
    if (!editName.trim() || isLocked || nameLoading) return;
    setNameLoading(true);
    try {
      await updateLeague(leagueId, { name: editName.trim() });
      setLeague((prev) => ({ ...prev, name: editName.trim() }));
      setNameSaved(true);
      setTimeout(() => setNameSaved(false), 2500);
    } catch (_) {
      setError('Failed to save league name.');
    } finally {
      setNameLoading(false);
    }
  }

  // ── Save points config ──
  async function savePoints() {
    if (isLocked || pointsLoading) return;
    const resolved = {};
    for (const { key } of ROUNDS) {
      const v = parseFloat(editPoints[key]);
      if (isNaN(v) || v < 0 || v > 1000) {
        setError(`Invalid points value for ${ROUNDS.find((r) => r.key === key)?.label}.`);
        return;
      }
      resolved[key] = v;
    }
    setPointsLoading(true);
    setError('');
    try {
      await updateLeague(leagueId, { pointsConfig: resolved });
      setLeague((prev) => ({ ...prev, pointsConfig: resolved }));
      setPointsSaved(true);
      setTimeout(() => setPointsSaved(false), 2500);
    } catch (_) {
      setError('Failed to save points configuration.');
    } finally {
      setPointsLoading(false);
    }
  }

  function handlePointChange(key, raw) {
    const val = parseFloat(raw);
    setEditPoints((prev) => ({ ...prev, [key]: raw === '' ? '' : (isNaN(val) ? raw : val) }));
  }

  // ── Invite link ──
  const inviteLink = league
    ? `${window.location.origin}/join/${league.inviteCode}`
    : '';

  async function copyInviteLink() {
    try {
      await navigator.clipboard.writeText(inviteLink);
      showToast('Invite link copied!');
    } catch (_) {
      setError('Could not copy — please copy the link manually.');
    }
  }

  // ── Rotate invite code ──
  async function handleRotateCode() {
    if (isLocked || rotatingCode) return;
    if (!window.confirm('Reset invite link? The old link will stop working immediately.')) return;
    setRotatingCode(true);
    try {
      const newCode = await rotateInviteCode(leagueId);
      setLeague((prev) => ({ ...prev, inviteCode: newCode }));
      showToast('Invite link reset!');
    } catch (_) {
      setError('Failed to reset invite link.');
    } finally {
      setRotatingCode(false);
    }
  }

  // ── Send invite email via Netlify Function ──
  async function handleSendInviteEmail(e) {
    e.preventDefault();
    const emailTrimmed = inviteEmail.trim();
    if (!emailTrimmed || emailSending) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrimmed)) {
      setEmailMsg('Please enter a valid email address.');
      return;
    }
    setEmailSending(true);
    setEmailMsg('');
    try {
      const userSnap = await getDoc(doc(db, 'users', currentUser.uid));
      const commissionerName = userSnap.exists() ? userSnap.data().username : '';

      const res = await fetch('/api/send-invite-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: emailTrimmed,
          leagueName: league.name,
          commissionerName,
          inviteLink,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to send.');
      }
      setEmailMsg('Invite sent!');
      setInviteEmail('');
    } catch (err) {
      setEmailMsg(err.message || 'Failed to send. The invite link is still valid.');
    } finally {
      setEmailSending(false);
    }
  }

  // ── Share button — exact spec message ──
  async function handleShare() {
    const msg = `I've made my World Cup Picks. Come make yours! ${inviteLink}`;
    if (navigator.share) {
      try { await navigator.share({ text: msg }); } catch (_) { /* user cancelled */ }
    } else {
      try {
        await navigator.clipboard.writeText(msg);
        showToast('Share message copied!');
      } catch (_) {
        setError('Could not copy — please copy the link manually.');
      }
    }
  }

  function showToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  }

  // ── Delete league ──
  async function handleDeleteLeague() {
    if (deleteLoading) return;
    setDeleteLoading(true);
    try {
      await deleteLeague(leagueId);
      navigate('/leagues', { replace: true });
    } catch (_) {
      setError('Failed to delete league. Please try again.');
      setDeleteLoading(false);
      setShowDeleteConfirm(false);
    }
  }

  // ── Guard renders ──
  if (notFound) {
    return <div className="manage-page"><div className="manage-not-found">League not found.</div></div>;
  }
  if (notCommissioner) {
    return <div className="manage-page"><div className="manage-not-found">Access denied — commissioner only.</div></div>;
  }
  if (!league) {
    return <div className="manage-page"><div className="manage-loading">Loading…</div></div>;
  }

  return (
    <div className="manage-page">
      {/* Header */}
      <header className="manage-header">
        <button className="manage-header-back" onClick={() => navigate(`/league/${leagueId}`)}>
          ← League
        </button>
        <span className="manage-header-title">Manage League</span>
      </header>

      <div className="manage-body">
        {error && <div className="manage-error">{error}</div>}

        {/* Lock banner */}
        {isLocked && (
          <div className="manage-lock-banner">
            Settings are locked — the tournament has kicked off. All fields are read-only.
          </div>
        )}

        {/* ── League Settings ── */}
        <div className="manage-section">
          <div className="manage-section-title">League Settings</div>
          <div className="manage-section-body">

            {/* League name */}
            <div className="manage-field">
              <label className="manage-label" htmlFor="manage-league-name">League Name</label>
              <div className="manage-input-row">
                <input
                  id="manage-league-name"
                  className="manage-input"
                  type="text"
                  value={editName}
                  onChange={(e) => { setEditName(e.target.value); setNameSaved(false); }}
                  maxLength={60}
                  disabled={isLocked}
                />
                {!isLocked && (
                  <button
                    className="manage-btn-save"
                    onClick={saveName}
                    disabled={nameLoading || !editName.trim() || editName.trim() === league.name}
                  >
                    {nameLoading ? 'Saving…' : 'Save'}
                  </button>
                )}
              </div>
              {nameSaved && <span className="manage-saved">✓ Saved</span>}
            </div>

            {/* Visibility — immutable, read-only display */}
            <div className="manage-field">
              <label className="manage-label">Visibility</label>
              <div className="manage-input-row">
                <input
                  className="manage-input"
                  value={league.isPublic ? 'Public' : 'Private'}
                  disabled
                  readOnly
                />
              </div>
              <span style={{ fontSize: 12, color: 'var(--muted)' }}>
                Visibility cannot be changed after creation.
              </span>
            </div>

          </div>
        </div>

        {/* ── Custom Points ── */}
        <div className="manage-section">
          <div className="manage-section-title">Points Per Round</div>
          <div className="manage-section-body">
            <p style={{ fontSize: 12, color: 'var(--muted)' }}>
              Unique correct pick bonus = 50% of the round's base points (automatic).
            </p>
            <div className="manage-points-grid">
              {ROUNDS.map(({ key, label }) => {
                const base = parseFloat(editPoints[key]) || 0;
                return (
                  <div className="manage-points-row" key={key}>
                    <span className="manage-points-label">{label}</span>
                    <div className="manage-points-input-wrap">
                      <input
                        className="manage-points-input"
                        type="number"
                        min="0"
                        step="1"
                        value={editPoints[key]}
                        onChange={(e) => handlePointChange(key, e.target.value)}
                        disabled={isLocked}
                        aria-label={`${label} points`}
                      />
                      <span className="manage-points-unit">pts</span>
                    </div>
                    <span className="manage-points-bonus">+{base * 0.5} unique</span>
                  </div>
                );
              })}
            </div>
            {!isLocked && (
              <button
                className="manage-btn-save"
                onClick={savePoints}
                disabled={pointsLoading}
                style={{ alignSelf: 'flex-end' }}
              >
                {pointsLoading ? 'Saving…' : 'Save Points'}
              </button>
            )}
            {pointsSaved && <span className="manage-saved">✓ Points configuration saved</span>}
          </div>
        </div>

        {/* ── Invite & Share ── */}
        <div className="manage-section">
          <div className="manage-section-title">Invite &amp; Share</div>
          <div className="manage-section-body">
            <div className="manage-field">
              <label className="manage-label">Invite Link</label>
              <div className="manage-invite-row">
                <div className="manage-invite-link" title={inviteLink}>{inviteLink}</div>
                <button className="manage-btn-copy" onClick={copyInviteLink}>
                  Copy
                </button>
                {!isLocked && (
                  <button
                    className="manage-btn-copy"
                    onClick={handleRotateCode}
                    disabled={rotatingCode}
                    title="Invalidate the current link and generate a new one"
                  >
                    {rotatingCode ? '…' : 'Reset'}
                  </button>
                )}
              </div>
            </div>

            {/* Email invite — optional, pre-lock only */}
            {!isLocked && (
              <form className="manage-email-form" onSubmit={handleSendInviteEmail}>
                <label className="manage-label">Send Invite by Email</label>
                <div className="manage-email-row">
                  <input
                    className="manage-input"
                    type="email"
                    placeholder="friend@example.com"
                    value={inviteEmail}
                    onChange={(e) => { setInviteEmail(e.target.value); setEmailMsg(''); }}
                    disabled={emailSending}
                  />
                  <button
                    className="manage-btn-send"
                    type="submit"
                    disabled={!inviteEmail.trim() || emailSending}
                  >
                    {emailSending ? 'Sending…' : 'Send'}
                  </button>
                </div>
                {emailMsg && (
                  <span className={`manage-email-msg${emailMsg === 'Invite sent!' ? ' manage-email-msg--ok' : ' manage-email-msg--err'}`}>
                    {emailMsg}
                  </span>
                )}
              </form>
            )}

            {/* Share button — disappears after lock per spec §6.5 */}
            {!isLocked && (
              <button className="manage-btn-share" onClick={handleShare}>
                📤 Share Invite
              </button>
            )}
          </div>
        </div>

        {/* ── Members ── */}
        <div className="manage-section">
          <div className="manage-section-title">
            Members ({league.memberCount})
          </div>
          <div className="manage-section-body">
            {members === null && (
              <div style={{ color: 'var(--muted)', fontSize: 14 }}>Loading members…</div>
            )}
            {members && members.length === 0 && (
              <div style={{ color: 'var(--muted)', fontSize: 14 }}>No members yet.</div>
            )}
            {members && members.length > 0 && (
              <div className="manage-members-list">
                {members.map((m) => {
                  const isComm = m.userId === league.commissionerId;
                  return (
                    <div className="manage-member-row" key={m.userId}>
                      <span
                        className={`manage-member-rank${m.rank === 1 ? ' manage-member-rank--gold' : ''}`}
                      >
                        #{m.rank}
                      </span>
                      <span className="manage-member-name">{m.username}</span>
                      {isComm && (
                        <span className="manage-member-badge">Commissioner</span>
                      )}
                      <span className="manage-member-pts">{m.totalPoints ?? 0} pts</span>
                      <span className="manage-member-picks">
                        {m.totalPicks ?? 0} picks
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
            <p style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
              Pick counts update as match results are confirmed.
            </p>
          </div>
        </div>

        {/* ── Danger Zone ── */}
        <div className="manage-section manage-section--danger">
          <div className="manage-section-title">Danger Zone</div>
          <div className="manage-section-body">
            {!showDeleteConfirm ? (
              <>
                <p style={{ fontSize: 13, color: 'var(--muted)', margin: 0 }}>
                  Permanently delete this league and all picks. This cannot be undone.
                </p>
                <button
                  className="manage-btn-danger"
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  Delete League
                </button>
              </>
            ) : (
              <>
                <p style={{ fontSize: 13, color: 'var(--muted)', margin: 0 }}>
                  Are you sure? This will permanently delete <strong>{league.name}</strong> and
                  all picks made by {league.memberCount} member{league.memberCount !== 1 ? 's' : ''}.
                </p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    className="manage-btn-danger"
                    onClick={handleDeleteLeague}
                    disabled={deleteLoading}
                  >
                    {deleteLoading ? 'Deleting…' : 'Yes, Delete League'}
                  </button>
                  <button
                    className="manage-btn-save"
                    onClick={() => setShowDeleteConfirm(false)}
                    disabled={deleteLoading}
                  >
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

      </div>

      {toast && <div className="manage-toast">{toast}</div>}
    </div>
  );
}
